import './App.scss'
import React, { useEffect, useState } from 'react'
import {
    Navigate,
    Route,
    Routes,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Container,
    LinearProgress,
    Stack,
    ThemeProvider,
} from '@mui/material'
import {
    InnsynContextProvider,
    useInnsynContext,
} from './context/InnsynContextProvider'
import { Home } from './pages/Home/Home'
import { Search } from './pages/Search/Search'
import LogoHeader from './components/LogoHeader'
import { theme } from './styles/theme'
import { NameSearch } from './pages/Search/NameSearch/NameSearch'
import { TagSearch } from './pages/Search/TagSearch/TagSearch'
import { SavedEarTags } from './pages/SavedEarTags/SavedEarTags'
import { SelectCutCombination } from './pages/Search/TagSearch/SelectCut/SelectCutCombination/SelectCutCombination'
import { SelectBaseCut } from './pages/Search/TagSearch/SelectCut/SelectBaseCut/SelectBaseCut'
import { SelectArea } from './pages/Search/TagSearch/SelectArea/SelectArea'
import { SelectDistrict } from './pages/Search/TagSearch/SelectDistrict/SelectDistrict'
import { SearchResults } from './pages/Search/SearchResults/SearchResults'
import TagDetailedInfo from './pages/Search/SearchResults/TagDetailedInfo'
import {
    INNSYN_BASE_PATH,
    INNSYN_SAVED_PATH,
    INNSYN_SEARCH_NAME_PATH,
    INNSYN_SEARCH_OEREMERKE_BASE_SNITT_PATH,
    INNSYN_SEARCH_OEREMERKE_DISTRIKT_ABSTRACT_PATH,
    INNSYN_SEARCH_OEREMERKE_INFO_ABSTRACT_PATH,
    INNSYN_SEARCH_OEREMERKE_OMRAADE_PATH,
    INNSYN_SEARCH_OEREMERKE_PATH,
    INNSYN_SEARCH_OEREMERKE_SNITT_COMBINATION_PATH,
    INNSYN_SEARCH_PATH,
    INNSYN_SEARCH_RESULTS_PATH,
} from './paths'
import { FormattedMessage } from 'react-intl'
import { LOCALES } from './i18n/locales'
import IntlProviderWrapper from './i18n/IntlProviderWrapper'

const supportedLocales = LOCALES
const localStorageLocale = localStorage.getItem(
    'reinmerker-innsyn-locale'
) as LOCALES

let defaultLocale = LOCALES.BOKMAAL

if (
    localStorageLocale &&
    Object.values(supportedLocales).includes(localStorageLocale)
) {
    defaultLocale = localStorageLocale
}

const NoMatch = () => {
    const navigate = useNavigate()

    return (
        <Container
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <h2 style={{ marginTop: '2rem' }}>
                <FormattedMessage id='feilmelding.ingen-url-treff' />{' '}
            </h2>

            <Button
                variant='contained'
                onClick={() => {
                    navigate(INNSYN_BASE_PATH)
                }}
                sx={{
                    position: 'absolute',
                    bottom: '4rem',
                    left: '2rem',
                    right: '2rem',
                }}
            >
                <FormattedMessage id='button.to-startpage' />
            </Button>
        </Container>
    )
}

const RenderAlert = ({ alert }: { alert: Alert }) => {
    const { clearAlert } = useInnsynContext()
    const [progress, setProgress] = useState<number>(0)

    useEffect(() => {
        if (!alert.ttl) {
            return
        }

        const timer = setInterval(() => {
            setProgress((oldProgress) => {
                if (oldProgress >= 100) {
                    clearInterval(timer)
                    return 100
                }

                return oldProgress + 1
            })
        }, alert.ttl / 100)

        return () => {
            clearInterval(timer)
        }
    }, [alert.ttl])

    return (
        <Alert
            onClose={() => clearAlert(alert.uniqueId)}
            severity={alert.severity}
            sx={{ position: 'relative' }}
        >
            {alert.body}
            {alert.ttl && (
                <LinearProgress
                    sx={{
                        position: 'absolute',
                        bottom: '0',
                        right: '0',
                        left: '0',
                        borderRadius: '0 0 4px 4px',
                    }}
                    variant='determinate'
                    value={progress}
                />
            )}
        </Alert>
    )
}

const Wrapper = ({ children }: { children: React.ReactNode }) => {
    const {
        isLoadingGeografi,
        isLoadingMessages,
        alerts,
        fetchBruker,
        fetchMerker,
        fetchSnitt,
        fetchGeografi,
        fetchTekster,
        fetchKonfigurasjon,
        createLoggedInSessionAndFetchBruker,
    } = useInnsynContext()

    const navigate = useNavigate()
    const [query, setQuery] = useSearchParams()

    const previousPath = localStorage.getItem('reinmerker-innsyn-route-v1')
    const previousPathTimeStamp = localStorage.getItem(
        'reinmerker-innsyn-route-timestamp-v1'
    )
    const validInterval = 1000 * 60 * 30

    const cleanupStoredRoute = () => {
        localStorage.removeItem('reinmerker-innsyn-route-v1')
        localStorage.removeItem('reinmerker-innsyn-route-timestamp-v1')
    }

    useEffect(() => {
        if (
            !previousPath || !previousPathTimeStamp || isNaN(Number(previousPathTimeStamp))) {
            return
        }

        const timestamp = Number(previousPathTimeStamp)
        const lessTimeThanIntervalHasPassed =
            Date.now() < timestamp + validInterval
        const oneSecondHasPassed = Date.now() > timestamp + 1000

        if (lessTimeThanIntervalHasPassed && oneSecondHasPassed) {
            cleanupStoredRoute()
            window.location.href = previousPath
            return
        }

        navigate(INNSYN_BASE_PATH)
        cleanupStoredRoute()
    }, [navigate, previousPath, previousPathTimeStamp])

    useEffect(() => {
        const sessionKey = query.get('Tempkey')

        if (sessionKey) {
            const nextQuery = new URLSearchParams(query)
            nextQuery.delete('Tempkey')
            setQuery(nextQuery, { replace: true })

            void createLoggedInSessionAndFetchBruker(sessionKey)
            return
        }

        void fetchBruker()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const fetchDataOnShowing = () => {
            if (document.visibilityState === 'visible') {
                void fetchMerker()
                void fetchSnitt()
                void fetchGeografi()
                void fetchTekster()
                void fetchKonfigurasjon()
            }
        }

        document.addEventListener('visibilitychange', fetchDataOnShowing)

        return () => {
            document.removeEventListener(
                'visibilitychange',
                fetchDataOnShowing
            )
        }
    }, [
        fetchMerker,
        fetchSnitt,
        fetchGeografi,
        fetchTekster,
        fetchKonfigurasjon,
    ])

    const inlineAlerts = alerts.filter((alert) => alert.displayInline)
    const floatingAlerts = alerts.filter((alert) => !alert.displayInline)

    return (
        <>
            <LogoHeader />

            <Container
                disableGutters
                sx={{ display: 'flex', flex: '1', flexDirection: 'column' }}
            >
                {inlineAlerts.length > 0 && (
                    <Stack spacing='1rem'>
                        {inlineAlerts.map((alert) => (
                            <Box key={alert.uniqueId}>
                                <RenderAlert alert={alert} />
                            </Box>
                        ))}
                    </Stack>
                )}

                <Container
                    sx={{
                        width: '95%',
                        position: 'absolute',
                        top: `${5.5 + 3 * inlineAlerts.length}rem`,
                        right: '2.5%',
                        left: '2.5%',
                    }}
                >
                    {floatingAlerts.map((alert) => (
                        <Box key={alert.uniqueId}>
                            <RenderAlert alert={alert} />
                        </Box>
                    ))}
                </Container>

                <Backdrop
                    sx={{
                        color: 'common.white',
                        zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
                    }}
                    open={isLoadingGeografi || isLoadingMessages}
                >
                    <CircularProgress color='inherit' />
                </Backdrop>

                {!isLoadingGeografi && !isLoadingMessages && (
                    <React.Fragment>{children}</React.Fragment>
                )}
            </Container>
        </>
    )
}

export const App = () => {
    return (
        <InnsynContextProvider defaultLocale={defaultLocale}>
            <IntlProviderWrapper>
                <ThemeProvider theme={theme}>
                    <Stack
                        className='App'
                        sx={{
                            height: '100%',
                            color: 'common.black',
                        }}>
                        <Wrapper>
                            <Routes>
                                <Route
                                    path={INNSYN_BASE_PATH}
                                    element={<Home/>}
                                />
                                <Route
                                    path={INNSYN_SEARCH_PATH}
                                    element={<Search/>}
                                />
                                <Route
                                    path={INNSYN_SAVED_PATH}
                                    element={<SavedEarTags/>}
                                />
                                <Route
                                    path={INNSYN_SEARCH_OEREMERKE_PATH}
                                    element={<TagSearch/>}
                                />
                                <Route
                                    path={
                                        INNSYN_SEARCH_OEREMERKE_BASE_SNITT_PATH
                                    }
                                    element={<SelectBaseCut/>}
                                />
                                <Route
                                    path={
                                        INNSYN_SEARCH_OEREMERKE_SNITT_COMBINATION_PATH
                                    }
                                    element={<SelectCutCombination/>}
                                />
                                <Route
                                    path={INNSYN_SEARCH_OEREMERKE_OMRAADE_PATH}
                                    element={<SelectArea/>}
                                />
                                <Route
                                    path={
                                        INNSYN_SEARCH_OEREMERKE_DISTRIKT_ABSTRACT_PATH
                                    }
                                    element={<SelectDistrict/>}
                                />
                                <Route
                                    path={INNSYN_SEARCH_NAME_PATH}
                                    element={<NameSearch/>}
                                />
                                <Route
                                    path={INNSYN_SEARCH_RESULTS_PATH}
                                    element={<SearchResults/>}
                                />
                                <Route
                                    path={
                                        INNSYN_SEARCH_OEREMERKE_INFO_ABSTRACT_PATH
                                    }
                                    element={<TagDetailedInfo/>}
                                />
                                <Route
                                    index
                                    element={
                                        <Navigate
                                            replace
                                            to={INNSYN_BASE_PATH}
                                        />
                                    }
                                />
                                <Route path='*' element={<NoMatch/>}/>
                            </Routes>
                        </Wrapper>
                    </Stack>
                </ThemeProvider>
            </IntlProviderWrapper>
        </InnsynContextProvider>
    )
}

export default App